import {
  RefObject,
  useEffect,
} from "react";

interface UseDragScrollProps {
  containerRef: RefObject<HTMLDivElement>;
  onDragStart?: () => void;
}

/**
 * Hook simples para permitir scroll horizontal via clicar, segurar e arrastar.
 * Não altera estilos e não usa refs globais para manter o impacto mínimo.
 */
export function useDragScroll({ containerRef, onDragStart }: UseDragScrollProps): void {
  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let startY = 0;
    let startScrollTop = 0;

    const handleMouseDown = (event: MouseEvent) => {
      // Somente botão esquerdo do mouse
      if (event.button !== 0) {
        return;
      }

      isDragging = true;
      if (onDragStart) {
        onDragStart();
      }
      startX = event.clientX;
      startScrollLeft = container.scrollLeft;
      startY = event.clientY;
      startScrollTop = container.scrollTop;
    };

    const handleMouseMove = (event: MouseEvent) => {
      if (!isDragging) {
        return;
      }

      event.preventDefault();

      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;

      container.scrollLeft = startScrollLeft - deltaX;
      container.scrollTop = startScrollTop - deltaY;
    };

    const handleMouseUpOrLeave = () => {
      isDragging = false;
    };

    container.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUpOrLeave);

    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUpOrLeave);
    };
  }, [containerRef, onDragStart]);
}
